/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.20.0
 * @since 1.17.0
 */

import * as Yup from 'yup';

import { getFormLocale } from '../../modules/form/const/formLocalization';
import { ICommonTranslation } from '../../modules/internationalization/types';
import { IAutoReportIdReducer } from './reducer';
import { EPageAutoReportIdType, EAutoReportIdFormatField as F } from './types';

export const initialAutoReportState: IAutoReportIdReducer = {
    id: '',
    isError: false,
    isLoading: false,
    name: '',
    type: EPageAutoReportIdType.Account,
    formatMeta: {
        reportFormats: [],
        [F.ReportFormat]: '',
        [F.NotifyByPhoneEnabled]: false,
        [F.NotifyByPhone]: '',
        [F.NotifyByEmailEnabled]: false,
        [F.NotifyByEmail]: '',
        [F.SendByEmailEnabled]: false,
        [F.SendByEmail]: '',
        [F.SendByEmailZip]: false,
        [F.SendByEmailZipPasswordEnabled]: false,
        [F.SendByEmailZipPassword]: '',
    },
    scheduleMeta: {},
};

/*
* -------------------------------------------
*          AUTOREPORT ID META
* -------------------------------------------
*/

export const AUTOREPORT_ID_GET_META_START = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_GET_META_START';
export const AUTOREPORT_ID_GET_META_SUCCESS = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_GET_META_SUCCESS';
export const AUTOREPORT_ID_GET_META_FAILURE = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_GET_META_FAILURE';

export const AUTOREPORT_ID_GET_TAB_META_START = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_GET_TAB_META_START';
export const AUTOREPORT_ID_GET_TAB_META_SUCCESS = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_GET_TAB_META_SUCCESS';
export const AUTOREPORT_ID_GET_TAB_META_FAILURE = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_GET_TAB_META_FAILURE';

/*
* -------------------------------------------
*          AUTOREPORT ID FORMAT
* -------------------------------------------
*/

const isSchemaRequired = (isEnabled: boolean, schema: Yup.StringSchema) =>
    isEnabled ? schema.required() : schema.nullable();

export const getAutoReportIdValidationSchema = (translationCommon: ICommonTranslation,
                                                isCdmAdditionalReportsEnabled: boolean) => {
    Yup.setLocale(getFormLocale(translationCommon.form.validation));

    return isCdmAdditionalReportsEnabled
        ? Yup.object().shape({
            [F.ReportFormat]: Yup.string(),
            [F.NotifyByPhoneEnabled]: Yup.boolean(),
            [F.NotifyByPhone]: Yup.string().length(10).when(F.NotifyByPhoneEnabled, isSchemaRequired),
            [F.NotifyByEmailEnabled]: Yup.boolean(),
            [F.NotifyByEmail]: Yup.string().email().when(F.NotifyByEmailEnabled, isSchemaRequired),
            [F.SendByEmailEnabled]: Yup.boolean(),
            [F.SendByEmail]: Yup.string().email().when(F.SendByEmailEnabled, isSchemaRequired),
        })
        : Yup.object().shape({
            [F.ReportFormat]: Yup.string(),
            [F.NotifyByPhoneEnabled]: Yup.boolean(),
            [F.NotifyByPhone]: Yup.string().length(10).when(F.NotifyByPhoneEnabled, isSchemaRequired),
            [F.NotifyByEmailEnabled]: Yup.boolean(),
            [F.NotifyByEmail]: Yup.string().email().when(F.NotifyByEmailEnabled, isSchemaRequired),
            [F.SendByEmailEnabled]: Yup.boolean(),
            [F.SendByEmail]: Yup.string().email().when(F.SendByEmailEnabled, isSchemaRequired),
            [F.SendByEmailZip]: Yup.boolean(),
            [F.SendByEmailZipPasswordEnabled]: Yup.boolean(),
            [F.SendByEmailZipPassword]: Yup.string().when(F.SendByEmailZipPasswordEnabled, isSchemaRequired),
        });
}


export const AUTOREPORT_ID_LOAD_FORMAT_META = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_LOAD_FORMAT_META';

export const AUTOREPORT_ID_UPDATE_FORMAT = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_UPDATE_FORMAT';

/*
* -------------------------------------------
*    AUTOREPORT ID SCHEDULE PARAMETERS
* -------------------------------------------
*/

export const AUTOREPORT_ID_LOAD_SCHEDULE_META = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_LOAD_SCHEDULE_META';

export const AUTOREPORT_ID_UPDATE_SCHEDULE = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_UPDATE_SCHEDULE';

/*
* -------------------------------------------
*    AUTOREPORT ID TABLE
* -------------------------------------------
*/

export const AUTOREPORT_ID_SAVE_TABLE = '@@b2b_portal_ui/autoReportId/AUTOREPORT_ID_SAVE_TABLE';
